"use client";

import { ArrowLeft } from "lucide-react";
import { useLayoutEffect } from "react";

import { AgentPromptBanner } from "./components/AgentPromptBanner";
import { ApiKeyCopyRow } from "./components/ApiKeyCopyRow";
import { ProviderCardGrid } from "./components/ProviderCardGrid";
import { WaitingStatus } from "./components/WaitingStatus";
import {
  getRequestAgentPrompt,
  getRequestInstallSnippet,
  getRequestRunSnippet,
  getTraceAgentPrompt,
} from "./constants/requestSnippets";
import { useObservabilitySetup } from "./hooks/useObservabilitySetup";

import { ApiKeyNameStepContent } from "@/components/PromptLayerApiKeys/ApiKeyModal/ApiKeyNameStepContent";
import CodeEditor from "@/components/CodeEditor";
import { LanguageNameMap, languages } from "@/components/CodeEditor/constants";
import { LanguageType } from "@/components/CodeEditor/types";
import { useOnboardingFlow } from "@/context/onboarding-flow-context";
import { useOnboardingSidebar } from "@/context/onboarding-sidebar-context";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { CopyButton } from "@/components/ui/copy-button";

const SetupTabButton = ({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "border-b-2 px-4 py-2 text-sm font-medium transition-colors",
      active
        ? "border-blue-600 text-blue-600"
        : "border-transparent text-gray-500 hover:text-gray-800",
    )}
  >
    {label}
  </button>
);

const LanguageTabs = ({
  selectedLanguage,
  availableLanguages,
  onLanguageChange,
}: {
  selectedLanguage: LanguageType;
  availableLanguages: LanguageType[];
  onLanguageChange: (language: LanguageType) => void;
}) => (
  <div className="flex items-center rounded-t-xl border-b border-gray-200 bg-white px-1">
    {availableLanguages.map(lang => (
      <button
        key={lang}
        type="button"
        onClick={() => onLanguageChange(lang)}
        className={cn(
          "border-b-2 px-3 py-2 text-sm font-medium transition-colors",
          selectedLanguage === lang
            ? "border-blue-500 text-blue-500"
            : "border-transparent text-gray-500 hover:text-gray-800",
        )}
      >
        {LanguageNameMap[lang]}
      </button>
    ))}
  </div>
);

const InstallStrip = ({ text }: { text: string }) => (
  <div className="flex items-center justify-between gap-3 border-b border-gray-200 bg-gray-50 px-4 py-2.5">
    <code className="truncate text-sm text-gray-700">{text}</code>
    <CopyButton text={text} iconSize="h-4 w-4" className="shrink-0" />
  </div>
);

const ManualSnippetCard = ({
  selectedLanguage,
  availableLanguages,
  onLanguageChange,
  installSnippet,
  code,
  editorHeight,
}: {
  selectedLanguage: LanguageType;
  availableLanguages: LanguageType[];
  onLanguageChange: (language: LanguageType) => void;
  installSnippet?: string;
  code: string;
  editorHeight: string;
}) => (
  <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
    <LanguageTabs
      selectedLanguage={selectedLanguage}
      availableLanguages={availableLanguages}
      onLanguageChange={onLanguageChange}
    />
    {installSnippet ? <InstallStrip text={installSnippet} /> : null}
    <CodeEditor
      code={code}
      selectedLanguage={selectedLanguage}
      languages={availableLanguages}
      showLanguageSelector={false}
      showRuntimeDetails={false}
      height={editorHeight}
      className="rounded-none border-0"
      editorOptions={{ readOnly: true, fontSize: 12 }}
    />
  </div>
);

export const ObservabilitySetup = () => {
  const { setOnboardingPath } = useOnboardingFlow();
  const { scrollToTop } = useOnboardingSidebar();

  const {
    setupMode,
    setSetupMode,
    selectedProviderId,
    setSelectedProviderId,
    keyName,
    setKeyName,
    apiKey,
    isGeneratingKey,
    language,
    setLanguage,
    received,
    statusLabel,
    selectedProvider,
    isSubmitLoading,
    isSkipLoading,
    isCompleted,
    handleGenerateApiKey,
    onSkip,
    onGoToItem,
    onFinish,
  } = useObservabilitySetup();

  useLayoutEffect(() => {
    scrollToTop();
  }, [scrollToTop]);

  const pageTitle =
    setupMode === "requests" ? "Start logging your LLM requests" : "Start sending traces";

  const installSnippet = getRequestInstallSnippet(language);
  const runSnippet = getRequestRunSnippet(language, apiKey);
  const traceLanguage = selectedProvider.languages.includes(language)
    ? language
    : selectedProvider.languages[0];
  const traceSnippet = selectedProvider.getSnippet(traceLanguage, apiKey);
  const agentPrompt =
    setupMode === "requests"
      ? getRequestAgentPrompt(apiKey)
      : getTraceAgentPrompt({
          apiKey,
          providerTitle: selectedProvider.title,
          docsUrl: selectedProvider.docsUrl,
          snippet: traceSnippet,
        });
  let finishTooltip =
    setupMode === "requests"
      ? "Send a request using the setup instructions. Finish becomes available after PromptLayer receives it."
      : "Send a trace using the setup instructions. Finish becomes available after PromptLayer receives it.";
  if (!apiKey) {
    finishTooltip = `Generate an API key, then ${setupMode === "requests" ? "send a request" : "send a trace"}. Finish becomes available after PromptLayer receives it.`;
  }

  return (
    <div className="w-full max-w-3xl pb-28">
      <div className="sticky top-0 z-10 -mx-2 mb-4 bg-white/95 px-2 py-3 backdrop-blur-sm">
        <button
          type="button"
          onClick={() => setOnboardingPath(null)}
          className="inline-flex items-center gap-2 rounded-md text-sm text-gray-500 transition-colors hover:text-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
      </div>

      <div className="space-y-2">
        <h1 className="text-3xl font-semibold tracking-tight text-gray-900">{pageTitle}</h1>
      </div>

      <div className="mt-8 space-y-8">
        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-gray-900">1. Get your API key</h2>

          {apiKey ? (
            <ApiKeyCopyRow apiKey={apiKey} />
          ) : (
            <div className="flex items-end gap-2">
              <div className="min-w-0 flex-1">
                <ApiKeyNameStepContent
                  keyName={keyName}
                  onKeyNameChange={setKeyName}
                  onSubmit={handleGenerateApiKey}
                />
              </div>
              <Button
                className="mb-1 shrink-0"
                onClick={handleGenerateApiKey}
                isLoading={isGeneratingKey}
                disabled={isGeneratingKey}
              >
                Generate API Key
              </Button>
            </div>
          )}
        </section>

        <section className="space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">2. Send your data</h2>
          </div>

          <WaitingStatus
            received={received}
            statusLabel={statusLabel}
            viewLabel={setupMode === "requests" ? "View Request" : "View Trace"}
            onViewItem={onGoToItem}
            isViewLoading={isSubmitLoading}
            isViewDisabled={isCompleted}
          />

          <div className="border-b border-gray-200">
            <SetupTabButton
              active={setupMode === "requests"}
              label="LLM Requests"
              onClick={() => setSetupMode("requests")}
            />
            <SetupTabButton
              active={setupMode === "traces"}
              label="Traces"
              onClick={() => setSetupMode("traces")}
            />
          </div>

          {setupMode === "traces" && (
            <ProviderCardGrid
              selectedProviderId={selectedProviderId}
              onSelectProvider={setSelectedProviderId}
            />
          )}

          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-900">
              Prefer to let your AI agent wire it up?
            </h3>
            <p className="text-sm text-gray-600">
              Open in Cursor or copy the prompt for Claude, Copilot, or your agent of choice. It
              will add {setupMode === "requests" ? "request logging" : "agent tracing"} to your
              codebase. This prompt includes your API key — treat it like a password.
            </p>
            <AgentPromptBanner prompt={agentPrompt} />
          </div>

          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-900">Or do it manually</h3>
            {setupMode === "requests" ? (
              <ManualSnippetCard
                selectedLanguage={language}
                availableLanguages={languages}
                onLanguageChange={setLanguage}
                installSnippet={installSnippet}
                code={runSnippet}
                editorHeight="260px"
              />
            ) : (
              <ManualSnippetCard
                selectedLanguage={traceLanguage}
                availableLanguages={selectedProvider.languages}
                onLanguageChange={setLanguage}
                code={traceSnippet}
                editorHeight="280px"
              />
            )}
          </div>
        </section>
      </div>

      <div className="fixed inset-x-0 bottom-0 z-20 border-t border-gray-200 bg-white/95 shadow-[0_-4px_16px_rgba(15,23,42,0.04)] backdrop-blur-sm">
        <div className="mx-auto flex w-full max-w-3xl items-center justify-end gap-2 px-4 py-3 sm:px-6">
          <Button
            variant="secondary"
            onClick={onSkip}
            disabled={isSkipLoading || isCompleted}
            isLoading={isSkipLoading}
          >
            Do This Later
          </Button>
          <BasicTooltip content={finishTooltip} side="top" shouldShow={!received && !isCompleted}>
            <span
              className="inline-flex"
              tabIndex={!received && !isCompleted ? 0 : undefined}
              aria-label={!received && !isCompleted ? finishTooltip : undefined}
            >
              <Button
                onClick={onFinish}
                disabled={!received || isSubmitLoading || isCompleted}
                isLoading={isSubmitLoading}
              >
                Finish
              </Button>
            </span>
          </BasicTooltip>
        </div>
      </div>
    </div>
  );
};
